import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { Drawer, Field } from '@/components/live';
import { callRpc } from '@/api/resources';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { toast } from '@/lib/toast';

/**
 * Delete a farmer, plot or KYC record - softly, and only with a reason (owner, 2026-09-21).
 *
 * Nothing is erased: `api.fn_soft_delete_record` marks the row deleted and keeps it, with who,
 * when and why, in the audit history. The server also refuses a farmer who still has plots and a
 * plot whose harvest is already in the stock records; those refusals are shown as written.
 *
 * Deleting needs the screen's `delete` right, which only a Project Admin holds. Everyone else
 * does not see the button at all.
 */
export function DeleteRecordButton({
  entity,
  id,
  screenKey,
  label,
  invalidate,
  onDeleted,
  deleteFn,
}: {
  entity: 'farmer' | 'plot' | 'kyc' | 'feature' | 'stakeholder' | 'land_document';
  id: string;
  screenKey: string;
  /** What is being deleted, for the confirmation: "farmer FRM-000004". */
  label: string;
  invalidate: string[];
  onDeleted: () => void;
  /** For a record deleted through its own writable view rather than fn_soft_delete_record. */
  deleteFn?: (reason: string) => Promise<unknown>;
}) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  if (!can(profile, screenKey, 'delete')) return null;

  async function remove() {
    if (reason.trim().length < 5) {
      setError('Say why this record is being deleted.');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      if (deleteFn) await deleteFn(reason.trim());
      else await callRpc('fn_soft_delete_record', { p_entity: entity, p_id: id, p_reason: reason.trim() });
      await Promise.all(invalidate.map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
      toast.success(`Deleted ${label}. It stays in the audit history.`);
      setConfirming(false);
      setReason('');
      onDeleted();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That could not be deleted.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button className="danger" onClick={() => { setError(undefined); setConfirming(true); }} type="button">
        <Trash2 size={16} /> Delete
      </button>
      <Drawer
        footer={
          <>
            <button onClick={() => setConfirming(false)} type="button">Keep it</button>
            <button className="primary danger" disabled={busy} onClick={() => void remove()} type="button">
              <Trash2 size={16} /> {busy ? 'Deleting...' : 'Delete'}
            </button>
          </>
        }
        onClose={() => setConfirming(false)}
        open={confirming}
        subtitle="The record leaves every list, but is never erased: it stays in the audit history with your reason."
        title={`Delete ${label}?`}
      >
        <Field error={error} label="Reason" required>
          <textarea autoFocus onChange={(event) => setReason(event.target.value)} rows={4}
                    placeholder="e.g. Duplicate registration of FRM-000012" value={reason} />
        </Field>
      </Drawer>
    </>
  );
}
